import React from 'react'

import Toaster from '../../ui/components/layout/toaster'

// Define toast types
type ToastType = 'success' | 'error'

// Interface for toast configuration
interface ToastConfig {
  message: string
  type?: ToastType
  duration?: number
}

// Context type
interface ToasterContextType {
  showError: (message: string, duration?: number) => void
  showSuccess: (message: string, duration?: number) => void
  hide: () => void
}

const ToasterContext = React.createContext<ToasterContextType | null>(null)

// Custom hook with type safety
function useToaster() {
  const context = React.useContext(ToasterContext)
  if (!context) {
    throw new Error('useToaster must be used within a ToasterProvider')
  }
  return context
}

const DEFAULT_TOASTER_DURATION = 3000 // in ms
const ToasterProvider = ({ children }: { children: React.ReactNode }) => {
  const [toast, setToast] = React.useState<ToastConfig | null>(null)
  const timerRef = React.useRef<number>()

  const hide = React.useCallback(() => {
    setToast(null)
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
  }, [])

  const show = React.useCallback((
    message: string,
    type: ToastType = 'success',
    duration: number = DEFAULT_TOASTER_DURATION
  ) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    setToast({ message, type, duration })

    if (duration > 0) {
      window.setTimeout(() => {
        hide()
      }, duration)
    }
  }, [hide])

  // Convenience methods for different toast types
  const showSuccess = React.useCallback((message: string, duration?: number) => {
    show(message, 'success', duration)
  }, [show])

  const showError = React.useCallback((message: string, duration?: number) => {
    show(message, 'error', duration)
  }, [show])

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  const value = React.useMemo(() => ({
    showSuccess,
    showError,
    hide
  }), [showSuccess, showError, hide])

  return (
    <ToasterContext.Provider value={value}>
      {children}
      {toast && <Toaster {...toast} onClose={hide} />}
    </ToasterContext.Provider>
  )
}

export {
  ToasterProvider,
  useToaster,
  type ToastType,
  type ToastConfig,
}
